import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Card,
  Statistic,
  Input,
  Select,
  DatePicker,
  Badge,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkOrderAside from "./workOrderAside";
import { formatCurrency } from "../../utils/Utils";
import { mockWorkOrders } from "../../data/mockWorkOrders";

const { Option } = Select;
const { RangePicker } = DatePicker;

const FinalizedWorkOrders: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    contractType: "",
    organisation: "",
    contractor: "",
    woValueRange: "",
    dateRange: null as any,
    slaStatus: "",
  });

  useEffect(() => {
    loadWorkOrders();
  }, []);

  const loadWorkOrders = () => {
    setLoading(true);
    try {
      // Filter work orders to show only accepted ones (approved for execution)
      const executionWorkOrders = mockWorkOrders.filter(
        (wo) => wo.status === "Accepted"
      );
      setWorkOrders(executionWorkOrders);
    } catch (error) {
      console.error("Error loading work orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Accepted: "success",
      Finalized: "success",
      "In Execution": "warning",
      Rejected: "error",
      "Under Negotiation": "processing",
    };
    return statusColors[status] || "default";
  };

  const handleViewWorkOrder = (record: any) => {
    navigate(`/view-work-order-details/${record.workOrderId}`);
  };

  const resetFilters = () => {
    setFilters({
      contractType: "",
      organisation: "",
      contractor: "",
      woValueRange: "",
      dateRange: null,
      slaStatus: "",
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  // KPI calculations
  const totalValue = workOrders.reduce((sum, wo) => sum + wo.woValue, 0);
  const acceptedCount = workOrders.filter(
    (wo) => wo.status === "Accepted"
  ).length;
  const avgSlaAge =
    workOrders.length > 0
      ? Math.round(
          workOrders.reduce((sum, wo) => sum + wo.slaAge, 0) / workOrders.length
        )
      : 0;

  const columns: TableColumnsType<any> = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 200,
      render: (text: string, record: any) => (
        <span
          className="inlineTableCellLink"
          id={`woStatus-${record?.workOrderId}`}
          onClick={() => handleViewWorkOrder(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Work Order Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 280,
      render: (text, record) => (
        <div>
          <Link
            to={`/view-work-order-details/${record.workOrderId}`}
            className="fw-bold text-primary"
          >
            {text}
          </Link>
          <div className="text-muted small mt-1">
            {record.siteName}
            {record.projectName && ` • ${record.projectName}`}
          </div>
        </div>
      ),
    },
    {
      title: "Organisation",
      dataIndex: "orgName",
      key: "orgName",
      width: 180,
    },
    // {
    //   title: "Contractor",
    //   dataIndex: "contractorName",
    //   key: "contractorName",
    //   width: 200,
    // },
    {
      title: "Contract Type",
      dataIndex: "contractType",
      key: "contractType",
      width: 140,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "WO Value",
      dataIndex: "grossValue",
      key: "grossValue",
      width: 150,
      align: "right",
      render: (value) => <strong>{formatCurrency(value, "INR")}</strong>,
      sorter: (a, b) => a.grossValue - b.grossValue,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => (
        <Tag
          color={getStatusColor(status)}
          icon={status === "Accepted" ? <CheckCircleOutlined /> : null}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Date Released",
      dataIndex: "dateReleased",
      key: "dateReleased",
      width: 140,
      render: (text) => dayjs(text).format("DD-MMM-YYYY"),
      sorter: (a, b) =>
        dayjs(a.dateReleased).unix() - dayjs(b.dateReleased).unix(),
    },
    {
      title: "Start Date",
      key: "startDate",
      width: 140,
      render: (_, record) => {
        if (record.status === "Accepted") {
          // Show the planned start date for execution
          return dayjs(record.plannedStartDate).format("DD-MMM-YYYY");
        }
        return "-";
      },
    },
    {
      title: "Contract Owner",
      dataIndex: "workOrderOwnerName",
      key: "workOrderOwnerName",
      width: 150,
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 150,
    //   render: (_, record) => (
    //     <Space wrap>
    //       <Button
    //         type="link"
    //         size="small"
    //         icon={<Icon name="eye" />}
    //         onClick={() => handleViewWorkOrder(record)}
    //       >
    //         View
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="finalized-work-orders"
      title="Finalized Work Orders"
      columns={columns}
      loading={loading}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkOrderAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1800,
        showSearch: true,
        searchPlaceholder: "Search by WO#, Title, or Organisation...",
        kpiWidgets: [
          {
            title: "Total In Execution",
            value: workOrders.length,
            icon: <CheckCircleOutlined />,
            color: "#52c41a",
          },
          {
            title: "Total Value",
            value: formatCurrency(totalValue, "INR"),
            icon: <DollarOutlined />,
            color: "#52c41a",
          },
          {
            title: "Accepted Count",
            value: acceptedCount,
            icon: <CalendarOutlined />,
            color: "#1890ff",
          },
          {
            title: "Avg SLA Age",
            value: `${avgSlaAge} days`,
            icon: <ClockCircleOutlined />,
            color: "#faad14",
          },
        ],
        advancedFilter: {
          visible: drawerVisible,
          onClose: () => setDrawerVisible(false),
          onOpen: () => setDrawerVisible(true),
          content: (
            <div className="p-4">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Contract Type</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contract Type"
                    value={filters.contractType}
                    onChange={(value) =>
                      setFilters({ ...filters, contractType: value })
                    }
                  >
                    <Option value="Fixed Price">Fixed Price</Option>
                    <Option value="Time & Material">Time & Material</Option>
                    <Option value="Cost Plus">Cost Plus</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Organisation</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Organisation"
                    value={filters.organisation}
                    onChange={(value) =>
                      setFilters({ ...filters, organisation: value })
                    }
                  >
                    <Option value="ABC Corp">ABC Corp</Option>
                    <Option value="XYZ Ltd">XYZ Ltd</Option>
                    <Option value="DEF Industries">DEF Industries</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Contractor</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contractor"
                    value={filters.contractor}
                    onChange={(value) =>
                      setFilters({ ...filters, contractor: value })
                    }
                  >
                    <Option value="TechBuild Solutions">
                      TechBuild Solutions
                    </Option>
                    <Option value="InfraMax Ltd">InfraMax Ltd</Option>
                    <Option value="BuildPro Services">BuildPro Services</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">WO Value Range</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Value Range"
                    value={filters.woValueRange}
                    onChange={(value) =>
                      setFilters({ ...filters, woValueRange: value })
                    }
                  >
                    <Option value="0-100000">₹0 - ₹1 Lakh</Option>
                    <Option value="100000-500000">₹1 Lakh - ₹5 Lakh</Option>
                    <Option value="500000-1000000">₹5 Lakh - ₹10 Lakh</Option>
                    <Option value="1000000+">₹10 Lakh+</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Date Range</label>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={filters.dateRange}
                    onChange={(dates) =>
                      setFilters({ ...filters, dateRange: dates })
                    }
                  />
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button
                  type="primary"
                  onClick={applyFilters}
                  style={{ flex: 1 }}
                >
                  Apply Filters
                </Button>
                <Button onClick={resetFilters} style={{ flex: 1 }}>
                  Reset
                </Button>
              </div>
            </div>
          ),
        },
      }}
    />
  );
};

export default FinalizedWorkOrders;
